import type * as BasketUpdated from '@ori-events/basket-updated';
import type * as UpdateOfferItem from '@ori-events/update-offer-item';
import { isUpdateOfferItemFailedResponse, Name as ErrorEventName } from '@ori-events/update-offer-item-failed';
import type { Data as ErrorData } from '@ori-events/update-offer-item-failed';
import type { PositionData } from '@ori/analytics-data';
import { HttpMethod, getErrorLogger } from '@ori/checkout-utils';

import { updateOfferItem } from '../api';
import { APP_NAME } from '../constants';
import { RequestUrl, type HandlerSettings } from '../types';
import { convertToCamelCase, getHttpRequestSettings } from '../utils';

type Data = PositionData & UpdateOfferItem.Data;

const logError = getErrorLogger(APP_NAME);

const dispatchErrorEvent = (detail: ErrorData) => {
  const errorEvent = new CustomEvent<ErrorData>(ErrorEventName, {
    detail,
  });
  window.dispatchEvent(errorEvent);
};

export async function updateOfferItemHandler(
  { detail: { choiceId, itemCode, quantity } }: CustomEvent<Data>,
  { client, sessionId, tenant }: HandlerSettings,
) {
  // STEP 1 - Update through API
  try {
    await updateOfferItem({
      client,
      itemCode,
      offerId: choiceId,
      quantity,
      sessionId,
      tenant,
    });
  } catch (error: unknown) {
    const message = String(error);
    logError(`Error while updating offer item in update-offer-item event: ${message}`);
    dispatchErrorEvent({
      isSuccess: false,
      localizedMessage: '',
      message: 'Updating of offer item failed via API call',
    });

    return false;
  }

  // STEP 2 - Update shopping context via Online endpoint
  const response = await fetch(RequestUrl.GetShoppingContext, getHttpRequestSettings(undefined, HttpMethod.Post));

  const data = convertToCamelCase<BasketUpdated.Data>((await response.json()) as object);

  if (isUpdateOfferItemFailedResponse(data)) {
    dispatchErrorEvent(data);

    return false;
  }

  return data;
}
